import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  InputGroup,
  InputGroupText,
  Row,
  Table,
  FormGroup,
} from "reactstrap";
import { post, put, doPortChange, get } from "helpers/api_helper";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import OptionsMenu from "components/OptionsMenu";
//   import * as api from "../../../constants/api_constants";
import CustomPagination from "components/CustomPagination";
import SummaryCards from "components/SummaryCards";
import { showErrorToast, showSuccessToast } from "helpers/toastMessages";
import { CustomButton } from "components/CommonInputs/CustomButtons/CustomButton";
import * as api from "../../constants/api_constants";
import { formattedDate } from "helpers/utils/dateUtils";

const Tickets = () => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = useCallback(async (query = "", page = 1) => {
    try {
      const response = await post(api.GET_ALL_TICKETS, {
        page: 0,
        pageSize: 10,
        sortBy: "ticketCode",
        direction: "ASC",
      });
      if (response?.data?.data) {
        setTickets(response.data.data);
        setTotalPages(response?.data?.totalPages || 0);
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
    }
  }, []);

  const getStatusLabelAndColor = (status) => {
    switch (status) {
      case "OPEN":
        return { label: "Open", color: "text-success" }; // Use green for Open
      case "IN_PROGRESS":
        return { label: "In Progress", color: "text-info" }; // Use blue for In Progress
      case "ASSIGNED":
        return { label: "Assigned", color: "text-primary" }; // Use primary color (e.g., blue) for Assigned
      case "RESOLVED":
        return { label: "Resolved", color: "text-muted" }; // Use gray for Resolved
      case "REOPEN":
        return { label: "Reopen", color: "text-warning" }; // Use orange for Reopen
      case "ESCALATED":
        return { label: "Escalated", color: "text-danger" }; // Use red for Escalated
      case "CLOSED":
        return { label: "Closed", color: "text-muted" }; // Use gray for Closed
      case "DUPLICATE":
        return { label: "Duplicate", color: "text-secondary" }; // Use secondary color (e.g., light gray) for Duplicate
      case "INVALID":
        return { label: "Invalid", color: "text-danger" }; // Use red for Invalid
      default:
        return { label: "Unknown", color: "text-black" }; // Default case
    }
  };

  const getPriorityLabelAndColor = (priority) => {
    switch (priority) {
      case "HIGH":
        return { label: "High", color: "text-danger" }; // Use red for high priority
      case "MEDIUM":
        return { label: "Medium", color: "text-warning" }; // Use orange for medium priority
      case "LOW":
        return { label: "Low", color: "text-success" }; // Use green for low priority
      default:
        return { label: "Unknown", color: "text-black" }; // Default case
    }
  };

  const summaryData = [
    {
      iconClass: "fas fa-exclamation-circle text-success",
      label: "Open",
      value: 0,
    },
    {
      iconClass: "fas fa-spinner text-info",
      label: "In Progress",
      value: 0,
    },
    {
      iconClass: "fas fa-check-circle text-muted",
      label: "Closed",
      value: 0,
    },
    {
      iconClass: "fas fa-exclamation-triangle text-danger",
      label: "Re Open",
      value: 0,
    },
    {
      iconClass: "fas fa-circle-check text-warning",
      label: "Solved",
      value: 0,
    },
  ];

  const handleEdit = useCallback(
    (id) => {
      navigate(`/add-ticket/${id}`);
    },
    [navigate]
  );

  return (
    <>
      <div className="page-content">
        <Container fluid={true} className="py-0 px-0">
          <PageSubHeader
            heading={"My Tickets"}
            routePath={"Home"}
            pageName={"My Tickets"}
          />
          <Row className="px-4">
            <SummaryCards data={summaryData} />
            <Row>
              <Col>
                <Row></Row>
                <Row className="d-flex justify-content-between align-items-center py-2">
                  <Col xs={3}>
                    <InputGroup size="sm" className="elbert-search-group">
                      <Input
                        placeholder="Search by Ticket ID"
                        className="elbert-search"
                        //   onChange={handleSearch}
                        //   value={searchQuery}
                      />
                      <InputGroupText>
                        <span className="fa fa-search"></span>
                      </InputGroupText>
                    </InputGroup>
                  </Col>
                  <Col xs={3}></Col>
                  <Col
                    xs={2}
                    className="d-flex align-items-center justify-content-end"
                  >
                    <CustomButton
                      text={"+ Create "}
                      onClick={() => navigate("/add-ticket")}
                    />
                  </Col>
                </Row>

                <Row>
                  <Card>
                    <CardBody>
                      <div className="table-responsive">
                        <Table className="table mb-0">
                          <thead>
                            <tr>
                              <th>Ticket ID</th>
                              <th>Subject</th>
                              <th>Assigned To</th>
                              <th>Created On</th>
                              <th>Last updated On</th>
                              <th>Priority</th>
                              <th>Status</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tickets && tickets.length > 0
                              ? tickets.map((ticket, index) => {
                                  const {
                                    label: statusLabel,
                                    color: statusColor,
                                  } = getStatusLabelAndColor(
                                    ticket.ticketStatus
                                  );
                                  const {
                                    label: priorityLabel,
                                    color: priorityColor,
                                  } = getPriorityLabelAndColor(ticket.priority);

                                  return (
                                    <tr key={index}>
                                      <td>{ticket.ticketCode}</td>
                                      <td>{ticket.title}</td>
                                      <td>{ticket.assigned}</td>
                                      <td>{formattedDate(ticket.createdDate)}</td>
                                      <td>{formattedDate(ticket.updatedAt)}</td>
                                      <td className={priorityColor}>
                                        {priorityLabel}
                                      </td>
                                      <td className={statusColor}>
                                        {statusLabel}
                                      </td>
                                      <td>
                                        <div
                                          className="d-flex justify-content-center gap-3 align-items-center"
                                          style={{ textAlign: "center" }}
                                        >
                                          <i
                                            className="fa-solid fa-eye action-icon"
                                            title="View"
                                            onClick={() =>
                                              navigate(
                                                `/view-ticket/${ticket.ticketId}`
                                              )
                                            }
                                          ></i>
                                          {/* <i
                                            onClick={() =>
                                              handleEdit(ticket.ticketId)
                                            }
                                            className="fa-regular fa-pen-to-square action-icon"
                                            title="Edit"
                                          ></i> */}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })
                              : null}
                          </tbody>
                        </Table>
                      </div>
                    </CardBody>
                  </Card>
                </Row>
              </Col>
            </Row>
            {/* <CustomPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                /> */}
          </Row>
        </Container>
      </div>
    </>
  );
};

export default Tickets;
